import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Utensils, Wine, Store } from "lucide-react";
import { toast } from "sonner";
import { api, STAGES, ESTAB_TYPES, formatApiError } from "../lib/api";
import { PageHeader, Spinner } from "../components/UI";

const TYPE_ICONS = { restaurant: Utensils, bar: Wine, liquor_store: Store };

const empty = {
  name: "",
  type: "restaurant",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  latitude: "",
  longitude: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  pipeline_stage: "lead",
  brand_ids: [],
  notes: "",
};

export default function EstablishmentForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const b = await api.get("/brands");
        setBrands(b.data.items || []);
        if (editing) {
          const { data } = await api.get(`/establishments/${id}`);
          setForm({
            ...empty,
            ...data,
            latitude: data.latitude ?? "",
            longitude: data.longitude ?? "",
            brand_ids: data.brand_ids || [],
          });
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, editing]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleBrand = (bid) => {
    setForm((f) => ({
      ...f,
      brand_ids: f.brand_ids.includes(bid)
        ? f.brand_ids.filter((x) => x !== bid)
        : [...f.brand_ids, bid],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      };
      if (editing) {
        await api.patch(`/establishments/${id}`, payload);
        toast.success("Changes saved");
      } else {
        const { data } = await api.post("/establishments", payload);
        toast.success(`Added "${data.name}"`);
        navigate(`/establishments/${data.id}`);
        return;
      }
      navigate(`/establishments/${id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this establishment and all its visits?")) return;
    try {
      await api.delete(`/establishments/${id}`);
      toast.success("Establishment deleted");
      navigate("/establishments");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <Link to={editing ? `/establishments/${id}` : "/establishments"} className="btn-ghost mb-2" data-testid="back-button">
        <ArrowLeft size={16} /> Back
      </Link>
      <PageHeader
        testid="estab-form-header"
        title={editing ? "Edit establishment" : "New establishment"}
        subtitle={editing ? "Keep your account info sharp." : "Add a restaurant, bar, or liquor store to your book."}
      />

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-4" data-testid="estab-form">
        <div className="card p-6 lg:col-span-2 space-y-4">
          <div>
            <div className="label mb-1">Name</div>
            <input
              data-testid="estab-name"
              className="input-base"
              required
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="The Velvet Olive"
            />
          </div>

          <div>
            <div className="label mb-2">Type</div>
            <div className="grid grid-cols-3 gap-2">
              {ESTAB_TYPES.map((t) => {
                const Icon = TYPE_ICONS[t.value];
                const active = form.type === t.value;
                return (
                  <button
                    type="button"
                    key={t.value}
                    data-testid={`type-${t.value}`}
                    onClick={() => setField("type", t.value)}
                    className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-1.5 ${
                      active ? "border-brand-ink bg-brand-ink text-white" : "border-brand-border bg-white hover:bg-brand-muted"
                    }`}
                  >
                    <Icon size={20} strokeWidth={1.6} />
                    <span className="text-sm font-medium">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="label mb-1">Address</div>
              <input data-testid="estab-address" className="input-base" value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="123 Peachtree St." />
            </div>
            <div>
              <div className="label mb-1">City</div>
              <input data-testid="estab-city" className="input-base" value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder="Atlanta" />
            </div>
            <div>
              <div className="label mb-1">State</div>
              <input data-testid="estab-state" className="input-base" value={form.state} onChange={(e) => setField("state", e.target.value)} placeholder="GA" />
            </div>
            <div>
              <div className="label mb-1">ZIP</div>
              <input data-testid="estab-zip" className="input-base" value={form.zip_code} onChange={(e) => setField("zip_code", e.target.value)} placeholder="30303" />
            </div>
            <div>
              <div className="label mb-1">Latitude</div>
              <input data-testid="estab-lat" className="input-base" type="number" step="any" value={form.latitude} onChange={(e) => setField("latitude", e.target.value)} placeholder="33.749" />
            </div>
            <div>
              <div className="label mb-1">Longitude</div>
              <input data-testid="estab-lng" className="input-base" type="number" step="any" value={form.longitude} onChange={(e) => setField("longitude", e.target.value)} placeholder="-84.388" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="label mb-1">Contact name</div>
              <input data-testid="estab-contact-name" className="input-base" value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} />
            </div>
            <div>
              <div className="label mb-1">Contact phone</div>
              <input data-testid="estab-contact-phone" className="input-base" value={form.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} placeholder="(404) 555-0142" />
            </div>
            <div className="sm:col-span-2">
              <div className="label mb-1">Contact email</div>
              <input data-testid="estab-contact-email" className="input-base" type="email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} />
            </div>
          </div>

          <div>
            <div className="label mb-1">Notes</div>
            <textarea
              data-testid="estab-notes"
              className="input-base min-h-[100px]"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Best time to drop by, who to ask for, what they pour…"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-6">
            <div className="label mb-2">Pipeline stage</div>
            <div className="flex flex-wrap gap-2">
              {STAGES.map((s) => (
                <button
                  type="button"
                  key={s.value}
                  data-testid={`stage-pick-${s.value}`}
                  onClick={() => setField("pipeline_stage", s.value)}
                  className={`badge ${s.className} ${form.pipeline_stage === s.value ? "ring-2 ring-brand-ink ring-offset-2 ring-offset-white" : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="card p-6">
            <div className="label mb-2">Brands being pitched</div>
            <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
              {brands.length === 0 && (
                <div className="text-sm text-brand-mutedFg">No brands yet. Add some on the Brands page.</div>
              )}
              {brands.map((b) => (
                <label key={b.id} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    data-testid={`brand-toggle-${b.id}`}
                    checked={form.brand_ids.includes(b.id)}
                    onChange={() => toggleBrand(b.id)}
                    className="w-4 h-4 accent-brand-ink"
                  />
                  <span className="text-brand-ink">{b.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-establishment">
              <Save size={16} /> {saving ? "Saving…" : "Save"}
            </button>
            {editing && (
              <button type="button" onClick={remove} className="btn-outline text-stage-lostFg border-stage-lostBg" data-testid="delete-establishment">
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
